import { ModuleDocContent } from "./docsData";

export const engagementModuleDocs: Record<string, ModuleDocContent> = {
  "gamification": {
    title: "Badge Management",
    subtitle: "Badges & Achievement System",
    color: "rose",
    badge: "Engagement",
    description:
      "Create and manage badges to reward learners. Define badge name, icon, rarity, XP requirements, and active status. Learners earn badges displayed in their profiles.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Badge Management</strong> lets you create, edit, and manage badges that reward learner achievements. Each badge has a name, icon or custom image, rarity level, XP requirement, and active/inactive status.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/3d58de7d-2403-4074-baa5-a9ab102b8aad.png" alt="Badge Management List" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Badge Management list view</p></div>

      <section>
        <h2>Stats Cards</h2>
        <p>Four summary cards are displayed in the page header:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total Badges</strong></td>
              <td>Total number of badges created.</td>
            </tr>
            <tr>
              <td><strong>Active Badges</strong></td>
              <td>Badges currently earnable by learners.</td>
            </tr>
            <tr>
              <td><strong>Inactive Badges</strong></td>
              <td>Badges that are disabled but preserved.</td>
            </tr>
            <tr>
              <td><strong>Total Earned</strong></td>
              <td>Total number of badge awards across all learners.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Badge List</h2>
        <h3>Filters</h3>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Search</strong></td>
              <td>Search badges by name.</td>
            </tr>
            <tr>
              <td><strong>Rarity</strong></td>
              <td>All Rarities, Common, Rare, Epic, Legendary.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>All Status, Active, Inactive.</td>
            </tr>
            <tr>
              <td><strong>Sort By</strong></td>
              <td>Date, Name, XP.</td>
            </tr>
          </tbody>
        </table>

        <h3>Table Columns</h3>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Badge</strong></td>
              <td>Icon or uploaded image, badge name, and description.</td>
            </tr>
            <tr>
              <td><strong>Rarity</strong></td>
              <td>Color-coded pill: Common, Rare, Epic, Legendary.</td>
            </tr>
            <tr>
              <td><strong>XP Required</strong></td>
              <td>XP threshold to earn the badge.</td>
            </tr>
            <tr>
              <td><strong>Category</strong></td>
              <td>Badge category label.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>Clickable Active/Inactive toggle (opens confirmation modal).</td>
            </tr>
            <tr>
              <td><strong>Actions</strong></td>
              <td>Preview (eye icon), Edit (pencil), Delete (trash).</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Create / Edit Badge</h2>
        <p>Clicking <strong>Create Badge</strong> opens a full-page form with a live preview panel at the top showing the badge as you configure it.</p>

        <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/2b07ed17-f643-4cfa-be7b-82c6a3908b46.png" alt="Create Badge" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Create Badge form</p></div>
        <table>
          <thead>
            <tr>
              <th>Section</th>
              <th>Fields</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Basic Information</strong></td>
              <td>Badge Name (required), Description (required).</td>
            </tr>
            <tr>
              <td><strong>Appearance</strong></td>
              <td>Icon selector (Crown, Medal, Award, Star, Flame, Lightning, Target, Brain, Heart, Shield, Gift, Sparkles) and Color Scheme picker. Disabled when a custom image is uploaded.</td>
            </tr>
            <tr>
              <td><strong>Badge Details</strong></td>
              <td>Rarity (Common/Rare/Epic/Legendary), XP Required (number), Category (text).</td>
            </tr>
            <tr>
              <td><strong>Badge Image</strong></td>
              <td>Optional image upload (max 2 MB). Overrides icon and color when present.</td>
            </tr>
            <tr>
              <td><strong>Active Status</strong></td>
              <td>Toggle switch &mdash; users can earn this badge when active.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Badge Preview Modal</h2>
        <p>Clicking the eye icon on any badge opens a detailed preview showing the badge icon/image, name, description, rarity, XP, category, status, and creation date. From the preview, you can directly <strong>Edit</strong> or <strong>Delete</strong> the badge.</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Create tiered achievement badges</div>
            <div class="sz-use-case-flow">
              Create badges at different rarity levels (Common for 100 XP, Rare for 500 XP, Epic for 1000 XP, Legendary for 5000 XP) to give learners progressive goals to work toward.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Custom event badges</div>
            <div class="sz-use-case-flow">
              Upload a custom image for a special event badge (e.g., quarterly challenge). Set it to Active during the event period and Inactive afterward to preserve it as a collectible.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Use the live preview while creating badges to see exactly how they'll appear to learners. Start with a few core badges before expanding the collection.</p>
      </div>

    </div>`,
  },

  "user-achievements": {
    title: "Learner Achievements",
    subtitle: "User Milestones & Certificates",
    color: "rose",
    badge: "Engagement",
    description:
      "Track learner milestones across the platform. View XP earned, badges collected, and certificates awarded for courses, certifications, and learning paths. Click any learner to see their full achievement profile.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Learner Achievements</strong> provides an overview of all learner milestones across the platform. It displays platform-wide metrics, a ranked list of learners with their XP, badges, and certificates, and a detail page for each learner showing all earned badges and certificates organized by type.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/8bcd20c5-db42-42a2-8b48-c744ef0f6475.png" alt="Learner Achievements List" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Learner Achievements list view</p></div>

      <section>
        <h2>Stats Cards</h2>
        <p>Four summary cards are displayed in the page header:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Learners</strong></td>
              <td>Total number of active learners on the platform.</td>
            </tr>
            <tr>
              <td><strong>Total XP</strong></td>
              <td>Combined XP earned across all learners.</td>
            </tr>
            <tr>
              <td><strong>Badges</strong></td>
              <td>Total number of badges earned platform-wide.</td>
            </tr>
            <tr>
              <td><strong>Certificates</strong></td>
              <td>Total certificates awarded across courses, certifications, and learning paths.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Search</strong></td>
              <td>Search by learner name, email, or employee ID.</td>
            </tr>
            <tr>
              <td><strong>Sort By</strong></td>
              <td>Toggle sort by XP, Badges, or Certificates (descending order).</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Learner List</h2>
        <p>Each learner is displayed as a card row with the following information:</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Rank</strong></td>
              <td>Position based on selected sort criteria. Top 3 get gold, silver, and bronze badges.</td>
            </tr>
            <tr>
              <td><strong>Profile</strong></td>
              <td>Profile image (or initials), name, email, employee ID, level, and streak info.</td>
            </tr>
            <tr>
              <td><strong>XP</strong></td>
              <td>Total experience points earned.</td>
            </tr>
            <tr>
              <td><strong>Badges</strong></td>
              <td>Number of badges collected.</td>
            </tr>
            <tr>
              <td><strong>Course</strong></td>
              <td>Number of course certificates earned.</td>
            </tr>
            <tr>
              <td><strong>Cert</strong></td>
              <td>Number of certification certificates earned.</td>
            </tr>
            <tr>
              <td><strong>Path</strong></td>
              <td>Number of learning path certificates earned.</td>
            </tr>
          </tbody>
        </table>
        <p>Clicking any learner row navigates to their <strong>Achievement Detail</strong> page.</p>
      </section>

      <section>
        <h2>Achievement Detail Page</h2>
        <p>The detail page shows a full profile header with the learner's stats (XP, streak, badges, certificates, rank, level) and four tabbed sections:</p>
        <table>
          <thead>
            <tr>
              <th>Tab</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Badges</strong></td>
              <td>Grid of earned badges showing icon/image, name, description, XP required, category, and rarity (Common, Rare, Epic, Legendary). Searchable by badge name.</td>
            </tr>
            <tr>
              <td><strong>Course</strong></td>
              <td>Grid of course completion certificates with banner image, course title, and issue date. Searchable by course name.</td>
            </tr>
            <tr>
              <td><strong>Certification</strong></td>
              <td>Grid of certification certificates with banner image, certification title, and issue date. Searchable by certification name.</td>
            </tr>
            <tr>
              <td><strong>Learning Path</strong></td>
              <td>Grid of learning path certificates with banner image, path title, and issue date. Searchable by path name.</td>
            </tr>
          </tbody>
        </table>
        <p>Each tab shows a count in the tab label and includes a search input to filter results within that category.</p>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Review learner progress</div>
            <div class="sz-use-case-flow">
              Open Learner Achievements and sort by XP to see top performers. Click on a specific learner to view their full badge collection, course certificates, certification certificates, and learning path certificates.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Identify low engagement</div>
            <div class="sz-use-case-flow">
              Search for a specific employee by name or ID. Check their XP, badge count, and certificate count to identify learners who may need additional support or motivation.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Track certification completion</div>
            <div class="sz-use-case-flow">
              Click on a learner, switch to the Certification tab, and review all certification certificates earned. Use the search to find a specific certification.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Use the sort toggle to quickly switch between XP, Badges, and Certificates rankings to get different perspectives on learner engagement and progress.</p>
      </div>

    </div>`,
  },

  "leaderboard": {
    title: "Leaderboard",
    subtitle: "Competitive Rankings",
    color: "rose",
    badge: "Engagement",
    description:
      "View learner rankings by XP points, courses completed, certifications earned, and learning streaks. Features a Hall of Fame spotlight for top 3 performers and detailed per-user statistics.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p>The <strong>Leaderboard</strong> ranks all learners by XP points earned from course completions, certifications, and streaks. It displays a Hall of Fame for top 3 performers, a full rankings table, and a sidebar with the logged-in user's personal statistics.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/73c0f7bf-cbbe-4aa9-9985-7ad29d9da332.png" alt="Leaderboard" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Leaderboard details</p></div>

      <section>
        <h2>Your Stats (Header Cards)</h2>
        <p>Four cards in the header show the logged-in user's own performance:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Your Rank</strong></td>
              <td>Current position on the leaderboard.</td>
            </tr>
            <tr>
              <td><strong>XP Points</strong></td>
              <td>Total experience points earned.</td>
            </tr>
            <tr>
              <td><strong>Courses</strong></td>
              <td>Total courses completed.</td>
            </tr>
            <tr>
              <td><strong>Certifications</strong></td>
              <td>Total certifications earned.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Hall of Fame</h2>
        <p>The top 3 learners are highlighted in a special spotlight section:</p>
        <table>
          <thead>
            <tr>
              <th>Position</th>
              <th>Display</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>1st Place</strong></td>
              <td>Large featured card with glowing avatar border, "Champion" label, XP, and Level.</td>
            </tr>
            <tr>
              <td><strong>2nd &amp; 3rd Place</strong></td>
              <td>Compact side-by-side cards with rank badge, avatar, XP, and Level.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>All Rankings Table</h2>
        <p>Below the Hall of Fame, a searchable leaderboard table shows all learners:</p>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Rank</strong></td>
              <td>Position badge (Crown for #1, Medal for #2, Trophy for #3, number for others).</td>
            </tr>
            <tr>
              <td><strong>User</strong></td>
              <td>Avatar, name (with "You" pill for the logged-in user), and email.</td>
            </tr>
            <tr>
              <td><strong>Level</strong></td>
              <td>Current learner level.</td>
            </tr>
            <tr>
              <td><strong>Courses</strong></td>
              <td>Number of courses completed.</td>
            </tr>
            <tr>
              <td><strong>Certifications</strong></td>
              <td>Number of certifications earned.</td>
            </tr>
            <tr>
              <td><strong>Current Streak</strong></td>
              <td>Consecutive learning days (fire emoji).</td>
            </tr>
            <tr>
              <td><strong>Longest Streak</strong></td>
              <td>Best consecutive learning streak (fire emoji).</td>
            </tr>
            <tr>
              <td><strong>XP</strong></td>
              <td>Total experience points.</td>
            </tr>
          </tbody>
        </table>
        <p>Search by name or email. Pagination supports 10, 20, 30, or 50 items per page.</p>
      </section>

      <section>
        <h2>Right Sidebar</h2>
        <p>The sidebar displays four cards with personal details for the logged-in user:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Contents</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Your Statistics</strong></td>
              <td>Rank, Total XP, Level, Courses, Certifications, Longest Streak, Current Streak, Average Progress bar.</td>
            </tr>
            <tr>
              <td><strong>Your Courses</strong></td>
              <td>Up to 3 recent courses with status (Completed/In Progress), Level, XP, and progress bar.</td>
            </tr>
            <tr>
              <td><strong>Your Certifications</strong></td>
              <td>Up to 3 recent certifications with Passed/Not Passed status, Level, XP, and progress bar.</td>
            </tr>
            <tr>
              <td><strong>Climbing Tips</strong></td>
              <td>Static tips on earning XP and building streaks.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Track personal progress</div>
            <div class="sz-use-case-flow">
              Open the leaderboard to see your rank and XP. Check the sidebar for your recent courses and certifications, and use the Climbing Tips for ideas on improving your rank.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Identify top performers</div>
            <div class="sz-use-case-flow">
              Review the Hall of Fame to see the top 3 learners. Search by name in the full rankings to find specific employees and compare their XP, courses, and streak stats.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>The leaderboard is read-only. XP is earned automatically through course completions, certifications, and maintaining learning streaks.</p>
      </div>

    </div>`,
  },

  "reviews": {
    title: "Reviews Management",
    subtitle: "Course Reviews & Ratings",
    color: "rose",
    badge: "Engagement",
    description:
      "Moderate learner reviews for courses, certifications, and learning paths. Approve or flag reviews, toggle featured status, add admin responses, and export review data.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Reviews Management</strong> lets admins moderate star ratings and written reviews submitted by learners for courses, certifications, and learning paths. Approve, flag, respond to, or delete reviews from a centralized table with filters and export.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/31a07901-6639-448e-b8ba-a8131a044343.png" alt="Reviews List" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Reviews list view</p></div>

      <section>
        <h2>Stats Cards</h2>
        <p>Three summary cards are displayed in the page header:</p>
        <table>
          <thead>
            <tr>
              <th>Card</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Total</strong></td>
              <td>Total number of reviews submitted.</td>
            </tr>
            <tr>
              <td><strong>Approved</strong></td>
              <td>Reviews approved for display.</td>
            </tr>
            <tr>
              <td><strong>Flagged</strong></td>
              <td>Reviews flagged for attention.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Search</strong></td>
              <td>Search by name, email, or employee ID.</td>
            </tr>
            <tr>
              <td><strong>Review Type</strong></td>
              <td>All Types, Course, Certificate, Learning Path.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>All Status, Approved, Flagged, Deleted.</td>
            </tr>
            <tr>
              <td><strong>Rating</strong></td>
              <td>All Ratings, 5 Stars, 4 Stars, 3 Stars, 2 Stars, 1 Star.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Reviews Table</h2>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>User</strong></td>
              <td>Avatar, full name, employee ID, and email.</td>
            </tr>
            <tr>
              <td><strong>Target</strong></td>
              <td>Course, certification, or learning path name with type label.</td>
            </tr>
            <tr>
              <td><strong>Rating</strong></td>
              <td>Star rating display (1&ndash;5 stars).</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>Approved (green), Flagged (orange), Deleted (red).</td>
            </tr>
            <tr>
              <td><strong>Featured</strong></td>
              <td>Star toggle &mdash; click to feature/unfeature a review.</td>
            </tr>
            <tr>
              <td><strong>Date</strong></td>
              <td>Submission date.</td>
            </tr>
            <tr>
              <td><strong>Actions</strong></td>
              <td>Quick approve/flag button + dropdown menu with View Details, Add/Edit Response, and Delete.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Key Actions</h2>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Approve / Flag</strong></td>
              <td>Quick action button on each row. Flagged reviews show an orange badge; approved reviews show green.</td>
            </tr>
            <tr>
              <td><strong>View Details</strong></td>
              <td>Opens a modal showing the full review text, user info, star rating, target, helpful count, report count, and any existing admin response.</td>
            </tr>
            <tr>
              <td><strong>Add / Edit Response</strong></td>
              <td>Opens a modal to write an admin response (10&ndash;500 characters) displayed below the learner's review.</td>
            </tr>
            <tr>
              <td><strong>Featured toggle</strong></td>
              <td>Click the star icon to feature or unfeature a review.</td>
            </tr>
            <tr>
              <td><strong>Export</strong></td>
              <td>Header button to export all reviews (with current filters) to Excel.</td>
            </tr>
            <tr>
              <td><strong>Delete</strong></td>
              <td>Permanently delete a review with confirmation modal.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Weekly review moderation</div>
            <div class="sz-use-case-flow">
              Filter by "Flagged" status to see reviews needing attention. Review the full text, then approve legitimate reviews or delete inappropriate ones. Add admin responses to address constructive feedback.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Feature top reviews</div>
            <div class="sz-use-case-flow">
              Filter by 5 Stars and Approved status. Click the star icon to feature the best reviews so they appear prominently on course pages.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Export for reporting</div>
            <div class="sz-use-case-flow">
              Set filters for a specific course type and date range. Click Export to download an Excel file for stakeholder review meetings.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Use admin responses to publicly acknowledge positive feedback and address concerns raised in lower-rated reviews. This builds learner trust and shows responsiveness.</p>
      </div>

    </div>`,
  },
};
